import { GadgetSystemDominionId } from "../../database/DominionLiterals";
import { UniqueValidationId } from "../../database/validations/keys";
import { FieldType } from "../../type-system/FieldType";
import type { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyDominion } from "./ShopifyDominion";
import type { ConnectionModelSpecVersion } from "./ShopifyMetadata";
import { buildConnectionSpec } from "./ShopifyMetadata";
import {
  ShopifyBusinessEntityKey,
  ShopifyBusinessEntityToOrdersKey,
  ShopifyBusinessEntityToShopifyPaymentsAccountKey,
  ShopifyBusinessEntityToShopKey,
  ShopifyBusinessEntityUniquenessValidationKey,
  ShopifyOrderKey,
  ShopifyOrderToBusinessEntityKey,
  ShopifyPaymentsAccountKey,
  ShopifyShopKey,
} from "./keys";

const specs: ConnectionModelSpecVersion = {
  key: ShopifyBusinessEntityKey,
  "2024-10": {
    name: "Shopify Business Entity",
    fields: [
      {
        name: "ID",
        fieldType: FieldType.ID,
        key: `${ShopifyBusinessEntityKey}-system-id`,
        dominionID: GadgetSystemDominionId,
      },
      {
        name: "Admin GraphQL API ID",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "admin_graphql_api_id", graphQLFieldKey: "id" } },
        validations: {
          [ShopifyBusinessEntityUniquenessValidationKey]: {
            key: ShopifyBusinessEntityUniquenessValidationKey,
            specID: UniqueValidationId,
          },
        },
      },
      {
        name: "Company Name",
        dominionID: ShopifyDominion.id,
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "company_name",
            graphQLFieldKey: "companyName",
            graphQLOnlyField: true,
          },
        },
      },
      {
        name: "Display Name",
        dominionID: ShopifyDominion.id,
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "display_name",
            graphQLFieldKey: "displayName",
            graphQLOnlyField: true,
          },
        },
      },
      {
        name: "Primary",
        dominionID: ShopifyDominion.id,
        fieldType: FieldType.Boolean,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "primary",
            graphQLFieldKey: "primary",
            graphQLOnlyField: true,
          },
        },
      },
      {
        name: "Address",
        dominionID: ShopifyDominion.id,
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "address",
            graphQLFieldKey: "address",
            graphQLOnlyField: true,
            graphQLSelection: `
            address1
            address2
            city
            countryCode
            province
            zip
            `,
          },
        },
      },
      {
        name: "Orders",
        fieldType: FieldType.HasMany,
        key: ShopifyBusinessEntityToOrdersKey,
        configuration: {
          relatedModelKey: ShopifyOrderKey,
          inverseFieldKey: ShopifyOrderToBusinessEntityKey,
        },
      },
      {
        name: "Shopify Payments Account",
        fieldType: FieldType.BelongsTo,
        key: ShopifyBusinessEntityToShopifyPaymentsAccountKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "shopifyPaymentsAccount",
            graphQLSelection: `
            id
            `,
          },
        },
        configuration: {
          relatedModelKey: ShopifyPaymentsAccountKey,
        },
      },
      {
        name: "Shop",
        key: ShopifyBusinessEntityToShopKey,
        fieldType: FieldType.BelongsTo,
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
      },
    ],
    syncConfig: { type: "custom", resource: "businessEntities" },
  },
};

export const ShopifyBusinessEntity = ({
  apiVersion,
  frameworkVersionConstraint,
}: {
  apiVersion: ShopifyApiVersion;
  frameworkVersionConstraint?: string;
}) => buildConnectionSpec(specs, apiVersion, frameworkVersionConstraint);
